// ---------------------------------------------------------------------------
// Speak Up's own origin. Gate decisions D26, D27, D28; closes #474.
//
// WHY A SEPARATE HOSTNAME IS THE WHOLE FIX, and it is a browser property
// rather than a policy:
//
//   Supabase stores its session in localStorage, and localStorage is scoped to
//   an ORIGIN. `speakup.team` and `getstede.com` are different
//   origins, so the Speak Up pages cannot see a Stede session even on the same
//   phone, in the same browser, seconds apart.
//
//   MEASURED AT OOB-5 rather than assumed, by submitting the same form from
//   two origins of one dev server and decoding the Authorization header the
//   app actually sent:
//
//     app origin        alg ES256, role "authenticated", claims carry the
//                       reporter's EMAIL ADDRESS in plaintext
//     separate origin   alg HS256, role "anon", no sub, no email
//
//   #474 says "the access token is attached". The measurement is worse than
//   that: the claims identify the person by email. On its own origin the
//   request carries the anon key and nothing else.
//
//   Note what this is NOT. supabase-js always sends an Authorization header —
//   the anon key when there is no session — so "is the header present" proves
//   nothing. WHOSE TOKEN IT IS, is the question, and it has to be decoded.
//
// THIS ALSO CLOSES #329 ON ITS OWN STATED CONDITION. That entry recorded the
// thread link being built from `window.location.origin` as DELIBERATE, and
// said "revisit only if Speak Up gets its own hostname." It now has one, and
// the same line of code becomes correct for a new reason: a reporter who
// scanned a code on a wall gets a speakup.team link, which never resolves
// to Stede.
// ---------------------------------------------------------------------------

// Hostnames that serve ONLY Speak Up. Everything else serves the whole app.
//
// The env var exists so a Preview deployment can be pointed at the Speak Up
// experience without owning a second domain — and so this is testable at all
// before DNS exists.
// AMENDED Sep 4, 2026 -- D26 NAMED `speakup.dr-signs.net` AND THAT ADDRESS
// COULD NOT BE CREATED. The reason is recorded here rather than only in
// ISSUES.md, because the next person to read this line will wonder why the
// gate and the code disagree:
//
//   `dr-signs.net`'s nameservers are Wix's, and its email runs through
//   carrierzone.com. Colby has no Wix login, and moving the domain into
//   Cloudflare would have moved the company website AND the company email in
//   one act. So the subdomain the gate assumed was free was not reachable.
//
// `speakup.team` was bought instead, on Cloudflare's own registrar, so DNS is
// automatic and nothing touches D&R's mail. **D28 is unaffected and this is
// the part worth being clear about:** D28 says Speak Up must not LOOK like
// part of Stede and must still carry D&R's name. It does -- the header shows
// D&R's mark and "D&R Signs" beneath the title. The ADDRESS carrying no
// company name is if anything better for an anonymous reporting channel: a
// phone showing `speakup.team` over somebody's shoulder says nothing about
// where they work.
const CONFIGURED = (process.env.REACT_APP_SPEAKUP_HOST || '').trim().toLowerCase();

export const SPEAKUP_HOSTS = [
  'speakup.team',
  ...(CONFIGURED ? [CONFIGURED] : []),
];

// True when the browser is on Speak Up's own origin. Deliberately reads
// `window.location.hostname` at call time rather than caching: a cached value
// is one hot-reload away from being wrong in development, and the cost of
// reading it is nil.
export function isSpeakUpHost() {
  if (typeof window === 'undefined') return false;
  return SPEAKUP_HOSTS.includes(window.location.hostname.toLowerCase());
}

// The address to print on a flyer or a QR code. Falls back to the current
// origin so nothing breaks before DNS is live, and so a Preview deployment
// generates codes that actually work against itself.
export function speakUpOrigin() {
  if (typeof window === 'undefined') return '';
  if (isSpeakUpHost()) return window.location.origin;
  return SPEAKUP_HOSTS[0] ? `https://${SPEAKUP_HOSTS[0]}` : window.location.origin;
}

// The address a flyer or QR code points at. Kept HERE rather than beside the
// PDF builder on purpose: #57's entire cost was that the flyers could not be
// regenerated from source, so the URL is the one fact that must stay correct
// and testable. `jspdf` cannot be imported under jsdom without a TextEncoder
// polyfill, so a test that reached through the PDF module could not assert the
// URL at all — the check most worth having would have been the one blocked by
// the rendering library.
export function flyerUrlForBranch(slug) {
  return `${speakUpOrigin()}/speak-up?branch=${encodeURIComponent(slug)}`;
}
